import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useApp } from '../lib/app-context';
import {
  ALL_AMENITIES,
  AMENITY_LABEL,
  GENDER_LABEL,
  MODE_LABEL,
  TYPE_LABEL,
  mad,
} from '../lib/format';
import { Field, Notice, Select, TextInput, Toggle } from '../components/ui';
import { cityOptions, useCities } from '../lib/places';
import { MapView } from '../components/Map';
import type {
  GeocodeResult,
  Photo,
  PropertyDetail,
  PropertyType,
  RentalModeName,
} from '../lib/types';
import { t } from '../lib/i18n';

/** How much to trust a geocoded point, said plainly to the host. */
const PRECISION_LABEL: Record<GeocodeResult['precision'], string> = {
  address: t("Adresse précise"),
  street: t("Rue — ajustez si besoin"),
  district: t("Quartier — ajustez si besoin"),
  city: t("Centre-ville — déplacez le point"),
  approximate: 'Approximatif',
};

/**
 * The publish wizard.
 *
 * The listing is created on the server at the end of step 2, then progressively
 * updated: a host who drops out at step 5 comes back to a draft with their photos
 * in it rather than to nothing. Publishing is a separate, explicit step that the
 * server can refuse — it checks KYC, a verified phone, three photos, a price and a
 * pin, and returns which requirement failed.
 */

/** Kept in step with the server's MIN_PHOTOS / MAX_PHOTOS / MAX_VIDEOS. */
const MIN_PHOTOS = 4;
const MAX_PHOTOS = 10;
const MAX_VIDEOS = 3;
const MAX_VIDEO_MB = 40;

const STEPS = [
  t("Type"),
  t("Localisation"),
  t("Détails"),
  t("Équipements"),
  t("Photos"),
  t("Mode de location"),
  t("Règles"),
  t("Aperçu"),
] as const;

const TYPE_HINTS: Record<PropertyType, string> = {
  ENTIRE_PLACE: t("Appartement ou maison rien que pour le locataire"),
  PRIVATE_ROOM: t("Une chambre dans un logement partagé"),
  SHARED_ROOM: t("Un lit dans une chambre commune"),
};

type ModeDraft = { enabled: boolean; price: string; minStay: number };

/**
 * Bedly's fee on a host price, mirrored from the server so the wizard can show
 * the guest-facing number live as it is typed. The server recomputes it on every
 * quote and booking, and that value is the one that is ever charged — this is
 * only for the preview.
 */
const FEE_BPS: Record<RentalModeName, number> = {
  COLOCATION: 800,
  MONTHLY: 800,
  NIGHTLY: 1200,
};

const feeOn = (mode: RentalModeName, hostCentimes: number): number =>
  Math.round((hostCentimes * FEE_BPS[mode]) / 10_000);

export default function PropertyWizard() {
  const navigate = useNavigate();
  const { user, toast, reportError } = useApp();

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [videos, setVideos] = useState<{ id: string; url: string; contentType: string }[]>([]);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const cities = useCities();
  const [candidates, setCandidates] = useState<GeocodeResult[]>([]);
  const [locating, setLocating] = useState(false);
  const [geocodeFallback, setGeocodeFallback] = useState(false);
  const [located, setLocated] = useState(false);

  const [type, setType] = useState<PropertyType>('PRIVATE_ROOM');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [city, setCity] = useState(user?.city ?? 'Rabat');
  const [neighborhood, setNeighborhood] = useState('');
  const [address, setAddress] = useState('');
  const [lat, setLat] = useState('33.9925');
  const [lng, setLng] = useState('-6.8562');
  const [bedrooms, setBedrooms] = useState(1);
  const [beds, setBeds] = useState(1);
  const [bathrooms, setBathrooms] = useState(1);
  const [maxGuests, setMaxGuests] = useState(1);
  const [furnished, setFurnished] = useState(true);
  const [genderPolicy, setGenderPolicy] = useState('ANY');
  const [amenities, setAmenities] = useState<string[]>(['wifi_fiber', 'desk']);
  const [houseRules, setHouseRules] = useState('');
  const [rooms, setRooms] = useState<{ name: string; beds: number; maxGuests: number }[]>([]);
  const [modes, setModes] = useState<Record<RentalModeName, ModeDraft>>({
    COLOCATION: { enabled: false, price: '1500', minStay: 3 },
    MONTHLY: { enabled: true, price: '2400', minStay: 3 },
    NIGHTLY: { enabled: false, price: '350', minStay: 1 },
  });

  /**
   * Turns the typed address into a point on the map.
   *
   * The server restricts results to Morocco and falls back to the centre of the
   * chosen city when the geocoder finds nothing, so a host always gets something
   * to place and nudge rather than a dead end — which matters, because a missing
   * pin blocks publishing entirely.
   */
  const locate = async () => {
    setLocating(true);
    setErrors({});
    try {
      const response = await api.post<{ results: GeocodeResult[]; fallback: boolean }>('/geocode', {
        address,
        city,
      });
      setCandidates(response.results);
      setGeocodeFallback(response.fallback);
      const first = response.results[0];
      if (first) applyCandidate(first);
      else toast(t("Adresse introuvable. Placez le point à la main."), 'error');
    } catch (error) {
      reportError(error, t("La recherche d’adresse a échoué"));
    } finally {
      setLocating(false);
    }
  };

  const applyCandidate = (candidate: GeocodeResult) => {
    setLat(String(candidate.lat));
    setLng(String(candidate.lng));
    setLocated(true);
    // The geocoder usually names the neighbourhood more consistently than a host
    // would, but never overwrites something they typed themselves.
    if (candidate.neighborhood && !neighborhood) setNeighborhood(candidate.neighborhood);
    if (candidate.city && cities.some((known) => known.name === candidate.city)) {
      setCity(candidate.city);
    }
  };

  const saveDraft = async (): Promise<string | null> => {
    setErrors({});
    const payload = {
      type,
      title,
      description,
      city,
      neighborhood,
      lat: Number(lat),
      lng: Number(lng),
      ...(address ? { addressPrecise: address } : {}),
      bedrooms,
      beds,
      bathrooms,
      maxGuests,
      furnished,
      genderPolicy,
      amenities,
      houseRules,
    };
    try {
      if (propertyId) {
        await api.patch(`/properties/${propertyId}`, payload);
        return propertyId;
      }
      const response = await api.post<{ property: PropertyDetail }>('/properties', payload);
      setPropertyId(response.property.id);
      return response.property.id;
    } catch (error) {
      const apiError = reportError(error);
      if (apiError instanceof ApiError) setErrors(apiError.fieldErrors());
      return null;
    }
  };

  const next = async () => {
    setBusy(true);
    try {
      // The draft is persisted as soon as there is enough to save it.
      if (step === 2) {
        const id = await saveDraft();
        if (!id) return;
      }
      if (step >= 2 && propertyId) {
        const id = await saveDraft();
        if (!id) return;
      }
      if (step === 5 && propertyId) {
        for (const [mode, draft] of Object.entries(modes) as [RentalModeName, ModeDraft][]) {
          if (!draft.enabled) continue;
          await api.put(`/properties/${propertyId}/rental-modes`, {
            mode,
            priceCentimes: Number(draft.price || 0) * 100,
            minStay: draft.minStay,
            isActive: true,
          });
        }
        for (const room of rooms) {
          await api.post(`/properties/${propertyId}/rooms`, {
            name: room.name,
            beds: room.beds,
            maxGuests: room.maxGuests,
            bathroomType: 'SHARED',
          });
        }
        setRooms([]);
      }
      setStep((current) => Math.min(current + 1, STEPS.length - 1));
    } finally {
      setBusy(false);
    }
  };

  /** Several at once, in order, stopping at the cap rather than failing on it. */
  const uploadPhotos = async (files: File[]) => {
    let id = propertyId;
    if (!id) {
      id = await saveDraft();
      if (!id) return;
    }
    const room = MAX_PHOTOS - photos.length;
    if (files.length > room) {
      toast(t('{max} photos maximum : les {count} dernières n’ont pas été ajoutées.', { max: MAX_PHOTOS, count: files.length - room }));
    }
    setBusy(true);
    try {
      for (const file of files.slice(0, Math.max(0, room))) {
        const form = new FormData();
        form.append('file', file);
        const response = await api.upload<{ photo: Photo }>(`/properties/${id}/photos`, form);
        setPhotos((current) => [...current, response.photo]);
      }
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async (photoId: string) => {
    try {
      await api.delete(`/photos/${photoId}`);
      setPhotos((current) => current.filter((photo) => photo.id !== photoId));
    } catch (error) {
      reportError(error);
    }
  };

  const uploadVideo = async (file: File) => {
    // Caught here rather than after a long upload over a phone connection.
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      toast(t('Vidéo trop lourde : {max} Mo maximum. Filmez plus court ou en 720p.', { max: MAX_VIDEO_MB }), 'error');
      return;
    }
    let id = propertyId;
    if (!id) {
      id = await saveDraft();
      if (!id) return;
    }
    const form = new FormData();
    form.append('file', file);
    setUploadingVideo(true);
    try {
      const response = await api.upload<{ video: { id: string; url: string; contentType: string } }>(
        `/properties/${id}/videos`,
        form,
      );
      setVideos((current) => [...current, response.video]);
    } catch (error) {
      reportError(error);
    } finally {
      setUploadingVideo(false);
    }
  };

  const removeVideo = async (videoId: string) => {
    try {
      await api.delete(`/videos/${videoId}`);
      setVideos((current) => current.filter((video) => video.id !== videoId));
    } catch (error) {
      reportError(error);
    }
  };

  const publish = async () => {
    if (!propertyId) return;
    setBusy(true);
    try {
      await saveDraft();
      await api.post(`/properties/${propertyId}/submit`);
      setSubmitted(true);
      toast(t("Annonce envoyée pour vérification."), 'ok');
    } catch (error) {
      const apiError = reportError(error);
      if (apiError?.code === 'KYC_REQUIRED') navigate('/verification');
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="page page--narrow stack" style={{ paddingTop: 60 }}>
        <div className="card card--pad stack center">
          <span style={{ fontSize: 40 }}>✓</span>
          <h1 style={{ fontSize: 24 }}>{t("Annonce envoyée !")}</h1>
          <p className="muted small">
            {t("Elle sera en ligne après la visite de vérification Bedly, sous 48 h. Vous pouvez déjà la modifier depuis votre tableau de bord.")}
          </p>
          <Link className="btn" to="/hote">
            {t("Voir le tableau de bord")}
          </Link>
        </div>
      </div>
    );
  }

  const enabledModeCount = Object.values(modes).filter((mode) => mode.enabled).length;

  const setModeEnabled = (mode: RentalModeName, enabled: boolean) =>
    setModes((current) => ({ ...current, [mode]: { ...current[mode], enabled } }));

  return (
    <div className="page page--narrow stack">
      <div className="row row--between">
        <Link to="/hote" className="small">
          {t("← Quitter")}
        </Link>
        <span className="small muted">
          {t("Étape")} {step + 1}/{STEPS.length} — {STEPS[step]}
        </span>
      </div>

      <div className="steps">
        {STEPS.map((label, index) => (
          <span key={label} className={`steps__bar ${index <= step ? 'steps__bar--done' : ''}`} />
        ))}
      </div>

      <div className="card card--pad stack">
        {step === 0 ? (
          <>
            <h2>{t("Quel type de logement ?")}</h2>
            <div className="stack stack--tight">
              {(Object.keys(TYPE_LABEL) as PropertyType[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`chip ${type === option ? 'chip--on' : ''}`}
                  style={{ justifyContent: 'flex-start', padding: '14px 16px' }}
                  onClick={() => setType(option)}
                >
                  <span className="stack stack--tight" style={{ alignItems: 'flex-start' }}>
                    <span className="strong">{TYPE_LABEL[option]}</span>
                    <span className="tiny muted">{TYPE_HINTS[option]}</span>
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : null}

        {step === 1 ? (
          <>
            <h2>{t("Où se trouve le logement ?")}</h2>
            <Field label={t("Titre de l’annonce")} error={errors.title} hint={t("6 caractères minimum.")}>
              <TextInput
                value={title}
                onChange={setTitle}
                placeholder={t("Chambre meublée à Agdal, 10 min de l’UM5")}
              />
            </Field>
            <div className="row row--wrap" style={{ gap: 10 }}>
              <div style={{ flex: '1 1 150px' }}>
                <Field label={t("Ville")} error={errors.city}>
                  <Select
                    value={city}
                    onChange={setCity}
                    options={cityOptions(cities)}
                  />
                </Field>
              </div>
              <div style={{ flex: '1 1 150px' }}>
                <Field label={t("Quartier")} error={errors.neighborhood}>
                  <TextInput value={neighborhood} onChange={setNeighborhood} placeholder={t("Agdal")} />
                </Field>
              </div>
            </div>
            <Field
              label={t("Adresse exacte")}
              hint={t("Jamais publique. Elle sert à placer le logement sur la carte, et n’est communiquée au locataire qu’une fois la réservation confirmée.")}
              error={errors.addressPrecise}
            >
              <div className="row" style={{ gap: 8 }}>
                <TextInput
                  value={address}
                  onChange={setAddress}
                  placeholder={t("12, rue Jbel Bouiblane")}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void locate();
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn btn--ghost"
                  disabled={locating || address.trim().length < 3}
                  onClick={locate}
                >
                  {locating ? t("Recherche…") : t("Localiser")}
                </button>
              </div>
            </Field>

            {candidates.length > 0 ? (
              <div className="card card--pad stack stack--tight">
                <span className="field__label">
                  {geocodeFallback
                    ? t("Adresse introuvable — voici le centre de la ville")
                    : t("Quelle adresse est la bonne ?")}
                </span>
                {candidates.map((candidate) => (
                  <button
                    key={candidate.lat + ',' + candidate.lng}
                    type="button"
                    className={
                      'chip ' +
                      (Number(lat) === candidate.lat && Number(lng) === candidate.lng ? 'chip--on' : '')
                    }
                    style={{ justifyContent: 'flex-start', width: '100%', textAlign: 'start' }}
                    onClick={() => applyCandidate(candidate)}
                  >
                    <span className="stack stack--tight" style={{ alignItems: 'flex-start' }}>
                      <span className="small strong">{candidate.label}</span>
                      <span className="tiny muted">{PRECISION_LABEL[candidate.precision]}</span>
                    </span>
                  </button>
                ))}
              </div>
            ) : null}

            {located ? (
              <div className="stack stack--tight">
                <MapView
                  pins={[{ id: 'draft', lat: Number(lat), lng: Number(lng), label: '📍' }]}
                  centre={{ lat: Number(lat), lng: Number(lng) }}
                  precise
                  height={300}
                  zoom={16}
                  onPinMove={(nextLat, nextLng) => {
                    setLat(nextLat.toFixed(6));
                    setLng(nextLng.toFixed(6));
                  }}
                />
                <span className="tiny muted">
                  {t("Faites glisser le repère — ou touchez la carte — jusqu’à la porte du logement. Position exacte, visible de vous seul : sur la carte publique, les locataires verront une zone d’environ 300 m autour de ce point.")}
                </span>
              </div>
            ) : null}

            <details>
              <summary className="small muted" style={{ cursor: 'pointer' }}>
                {t("Saisir les coordonnées à la main")}
              </summary>
              <div className="row row--wrap" style={{ gap: 10, marginTop: 10 }}>
                <div style={{ flex: '1 1 140px' }}>
                  <Field label={t("Latitude")} error={errors.lat}>
                    <TextInput value={lat} onChange={setLat} inputMode="decimal" />
                  </Field>
                </div>
                <div style={{ flex: '1 1 140px' }}>
                  <Field label={t("Longitude")} error={errors.lng}>
                    <TextInput value={lng} onChange={setLng} inputMode="decimal" />
                  </Field>
                </div>
              </div>
            </details>

            <Notice tone="info">
              {t("La position publiée est volontairement décalée d’environ 300 m. L’adresse précise n’est partagée qu’après confirmation de la réservation.")}
            </Notice>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <h2>{t("Les essentiels")}</h2>
            {(
              [
                [t("Chambres"), bedrooms, setBedrooms],
                [t("Lits"), beds, setBeds],
                [t("Salles de bain"), bathrooms, setBathrooms],
                [t("Voyageurs max"), maxGuests, setMaxGuests],
              ] as const
            ).map(([label, value, setter]) => (
              <div key={label} className="row row--between">
                <span className="small strong">{label}</span>
                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setter(Math.max(label === 'Salles de bain' ? 0 : 1, value - 1))}
                  >
                    −
                  </button>
                  <span className="strong" style={{ minWidth: 24, textAlign: 'center' }}>
                    {value}
                  </span>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setter(value + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
            <Toggle on={furnished} onChange={setFurnished} label={t("Meublé")} />

            {/*
              How it is rented is asked here, next to what the place is, and the
              price step then shows only the prices those choices need — rather
              than three switches there that half the hosts never read.
            */}
            <div className="stack stack--tight">
              <span className="field__label">{t("Type de location")}</span>
              <Toggle
                on={modes.MONTHLY.enabled}
                onChange={(enabled) => setModeEnabled('MONTHLY', enabled)}
                label={t("Location au mois")}
              />
              <Toggle
                on={modes.NIGHTLY.enabled}
                onChange={(enabled) => setModeEnabled('NIGHTLY', enabled)}
                label={t("Location à la nuit")}
              />
              <Toggle
                on={modes.COLOCATION.enabled}
                onChange={(enabled) => {
                  setModeEnabled('COLOCATION', enabled);
                  // A preference about flatmates means nothing without flatmates.
                  if (!enabled) setGenderPolicy('ANY');
                }}
                label={t("Colocation (chambres partagées entre locataires)")}
              />
              {enabledModeCount === 0 ? (
                <span className="field__error">{t("Choisissez au moins un type de location.")}</span>
              ) : null}
            </div>

            {modes.COLOCATION.enabled ? (
              <Field label={t("Préférence de colocation")}>
                <Select
                  value={genderPolicy}
                  onChange={setGenderPolicy}
                  options={Object.entries(GENDER_LABEL).map(([value, label]) => ({ value, label }))}
                />
              </Field>
            ) : null}
            <Field label={t("Description")}>
              <textarea
                className="textarea"
                rows={5}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder={t("Chambre lumineuse dans un appartement calme, à dix minutes à pied du campus…")}
              />
            </Field>
          </>
        ) : null}

        {step === 3 ? (
          <>
            <h2>{t("Quels équipements ?")}</h2>
            <div className="row row--wrap" style={{ gap: 8 }}>
              {ALL_AMENITIES.map((amenity) => (
                <button
                  key={amenity}
                  type="button"
                  className={`chip ${amenities.includes(amenity) ? 'chip--on' : ''}`}
                  onClick={() =>
                    setAmenities((current) =>
                      current.includes(amenity)
                        ? current.filter((item) => item !== amenity)
                        : [...current, amenity],
                    )
                  }
                >
                  {AMENITY_LABEL[amenity]}
                </button>
              ))}
            </div>
          </>
        ) : null}

        {step === 4 ? (
          <>
            <h2>{t("Ajoutez des photos")}</h2>
            <p className="small muted" style={{ margin: 0 }}>
              {t("Entre")} {MIN_PHOTOS} et {MAX_PHOTOS} {t("photos, prises directement au téléphone : chaque pièce, la cuisine, la salle de bain, la vue. La première devient la couverture.")}
            </p>
            <label
              className="btn btn--ghost btn--block"
              style={{ cursor: photos.length >= MAX_PHOTOS ? 'not-allowed' : 'pointer' }}
              aria-disabled={photos.length >= MAX_PHOTOS}
            >
              {busy
                ? t("Envoi…")
                : photos.length >= MAX_PHOTOS
                  ? t('{max} photos — le maximum', { max: MAX_PHOTOS })
                  : t('+ Ajouter des photos ({count}/{max})', { count: photos.length, max: MAX_PHOTOS })}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/avif"
                multiple
                hidden
                disabled={busy || photos.length >= MAX_PHOTOS}
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  if (files.length) void uploadPhotos(files);
                  event.target.value = '';
                }}
              />
            </label>
            <div className="grid grid--tiles">
              {photos.map((photo, index) => (
                <div key={photo.id} className="card" style={{ position: 'relative' }}>
                  <img
                    src={photo.url}
                    alt=""
                    style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }}
                  />
                  {index === 0 ? (
                    <span className="badge badge--visited" style={{ position: 'absolute', top: 8, left: 8 }}>
                      {t("★ Couverture")}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className="media-remove"
                    aria-label={t("Retirer cette photo")}
                    onClick={() => void removePhoto(photo.id)}
                  >
                    {t("×")}
                  </button>
                </div>
              ))}
              {photos.length < MIN_PHOTOS
                ? Array.from({ length: MIN_PHOTOS - photos.length }, (_, index) => (
                    <div
                      key={`placeholder-${index}`}
                      className="card center muted small"
                      style={{ aspectRatio: '4/3', display: 'grid', placeItems: 'center' }}
                    >
                      {t("Photo")} {photos.length + index + 1}
                    </div>
                  ))
                : null}
            </div>
            {photos.length < MIN_PHOTOS ? (
              <Notice tone="warn">
                {t("Il manque")} {MIN_PHOTOS - photos.length} {t("photo(s) pour pouvoir publier.")}
              </Notice>
            ) : null}

            <h3 style={{ marginBottom: 0 }}>{t("Vidéos (facultatif)")}</h3>
            <p className="small muted" style={{ margin: 0 }}>
              {t("Jusqu’à")} {MAX_VIDEOS} {t("courtes vidéos pour faire visiter le logement —")} {MAX_VIDEO_MB} {t("Mo maximum chacune (environ une minute en 720p).")}
            </p>
            {/*
              The rule is written where the upload happens, not in terms nobody
              opens: a video with a phone number or text on it is a way around
              Bedly's messaging and fee, and the admin rejects the listing.
            */}
            <Notice tone="warn">
              <strong>{t("Important :")}</strong> {t("vos vidéos ne doivent contenir")} <strong>{t("aucun numéro de téléphone ni aucun texte")}</strong> {t("— coordonnées, prix, logo, lien, nom de réseau social. Une annonce dont une vidéo en contient sera")} <strong>{t("refusée par nos équipes")}</strong>.
            </Notice>
            <label
              className="btn btn--ghost btn--block"
              style={{ cursor: videos.length >= MAX_VIDEOS ? 'not-allowed' : 'pointer' }}
              aria-disabled={videos.length >= MAX_VIDEOS}
            >
              {uploadingVideo
                ? t("Envoi de la vidéo…")
                : videos.length >= MAX_VIDEOS
                  ? t('{max} vidéos — le maximum', { max: MAX_VIDEOS })
                  : t('+ Ajouter une vidéo ({count}/{max})', { count: videos.length, max: MAX_VIDEOS })}
              <input
                type="file"
                accept="video/mp4,video/quicktime,video/webm"
                hidden
                disabled={uploadingVideo || videos.length >= MAX_VIDEOS}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void uploadVideo(file);
                  event.target.value = '';
                }}
              />
            </label>
            {videos.length > 0 ? (
              <div className="grid grid--tiles">
                {videos.map((video) => (
                  <div key={video.id} className="card" style={{ position: 'relative' }}>
                    <video
                      src={video.url}
                      controls
                      playsInline
                      preload="metadata"
                      style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block', background: '#000' }}
                    />
                    <button
                      type="button"
                      className="media-remove"
                      aria-label={t("Retirer cette vidéo")}
                      onClick={() => void removeVideo(video.id)}
                    >
                      {t("×")}
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </>
        ) : null}

        {step === 5 ? (
          <>
            <h2>{t("Comment louez-vous ?")}</h2>
            <p className="small muted" style={{ margin: 0 }}>
              {t("Un prix et une durée minimale pour chaque type de location choisi à l’étape précédente.")}
            </p>
            {(Object.keys(modes) as RentalModeName[])
              .filter((mode) => modes[mode].enabled)
              .map((mode) => (
              <div key={mode} className="card card--pad stack stack--tight">
                <strong>{MODE_LABEL[mode]}</strong>
                {modes[mode].enabled ? (
                  <div className="row row--wrap" style={{ gap: 10 }}>
                    <div style={{ flex: '1 1 140px' }}>
                      <Field label={mode === 'NIGHTLY' ? t("Prix par nuit (DH)") : t("Prix par mois (DH)")}>
                        <TextInput
                          value={modes[mode].price}
                          onChange={(value) =>
                            setModes((current) => ({
                              ...current,
                              [mode]: { ...current[mode], price: value.replace(/\D/g, '') },
                            }))
                          }
                          inputMode="numeric"
                        />
                      </Field>
                    </div>
                    <div style={{ flex: '1 1 140px' }}>
                      <Field label={mode === 'NIGHTLY' ? t("Nuits minimum") : t("Mois minimum")}>
                        <Select
                          value={String(modes[mode].minStay)}
                          onChange={(value) =>
                            setModes((current) => ({
                              ...current,
                              [mode]: { ...current[mode], minStay: Number(value) },
                            }))
                          }
                          options={(mode === 'NIGHTLY' ? [1, 2, 3, 7] : [1, 3, 6, 9, 10, 12]).map(
                            (count) => ({ value: String(count), label: String(count) }),
                          )}
                        />
                      </Field>
                    </div>
                    {/* The host needs to see both numbers before they commit to one:
                        what lands in their hand, and what the guest is quoted. */}
                    <div
                      className="stack stack--tight"
                      style={{ flex: '1 1 220px', alignSelf: 'flex-end' }}
                    >
                      <div className="price-line">
                        <span className="small">{t("Vous recevez, en espèces")}</span>
                        <span className="small strong">
                          {mad(Number(modes[mode].price || 0) * 100)}
                        </span>
                      </div>
                      <div className="price-line">
                        <span className="small muted">
                          {t("Frais Bedly (")}{mode === 'NIGHTLY' ? '12' : '8'} %)
                        </span>
                        <span className="small muted">
                          +{mad(feeOn(mode, Number(modes[mode].price || 0) * 100))}
                        </span>
                      </div>
                      <div className="price-line price-line--total">
                        <span className="small">{t("Le locataire voit")}</span>
                        <span className="small">
                          {mad(
                            Number(modes[mode].price || 0) * 100 +
                              feeOn(mode, Number(modes[mode].price || 0) * 100),
                          )}
                          <span className="muted" style={{ fontWeight: 500 }}>
                            {mode === 'NIGHTLY' ? '/nuit' : '/mois'}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
            {enabledModeCount === 0 ? (
              <Notice tone="warn">{t("Activez au moins un mode de location avec un prix.")}</Notice>
            ) : null}

            {type !== 'ENTIRE_PLACE' ? (
              <div className="card card--pad stack stack--tight">
                <span className="strong">{t("Chambres à louer séparément (optionnel)")}</span>
                <span className="tiny muted">
                  {t("Ajoutez une chambre pour la louer indépendamment. Réserver le logement entier bloque automatiquement toutes les chambres, et inversement.")}
                </span>
                {rooms.map((room, index) => (
                  <div key={index} className="row" style={{ gap: 8 }}>
                    <TextInput
                      value={room.name}
                      onChange={(value) =>
                        setRooms((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, name: value } : item,
                          ),
                        )
                      }
                      placeholder={t("Chambre sur cour")}
                    />
                    <button
                      type="button"
                      className="btn btn--quiet btn--sm"
                      onClick={() => setRooms((current) => current.filter((_, i) => i !== index))}
                    >
                      {t("Retirer")}
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() =>
                    setRooms((current) => [
                      ...current,
                      { name: t('Chambre {number}', { number: current.length + 1 }), beds: 1, maxGuests: 1 },
                    ])
                  }
                >
                  {t("+ Ajouter une chambre")}
                </button>
              </div>
            ) : null}
          </>
        ) : null}

        {step === 6 ? (
          <>
            <h2>{t("Règles de la maison")}</h2>
            <Field label={t("Règles")}>
              <textarea
                className="textarea"
                rows={5}
                value={houseRules}
                onChange={(event) => setHouseRules(event.target.value)}
                placeholder={t("Non-fumeur. Calme après 22 h. Invités jusqu’à 22 h.")}
              />
            </Field>
            <Notice tone="info">
              {t("Les disponibilités se gèrent depuis le calendrier de l’annonce, après publication.")}
            </Notice>
          </>
        ) : null}

        {step === 7 ? (
          <>
            <h2>{t("Aperçu de votre annonce")}</h2>
            <div className="card">
              {photos[0] ? (
                <img
                  src={photos[0].url}
                  alt=""
                  style={{ width: '100%', aspectRatio: '16/10', objectFit: 'cover', display: 'block' }}
                />
              ) : null}
              <div className="card__body stack stack--tight">
                <span className="strong">{title || t("Titre de l’annonce")}</span>
                <span className="small muted">
                  {neighborhood || t("Quartier")}, {city}
                </span>
                <div className="row row--wrap" style={{ gap: 6 }}>
                  {(Object.keys(modes) as RentalModeName[])
                    .filter((mode) => modes[mode].enabled)
                    .map((mode) => (
                      <span key={mode} className="badge badge--verified">
                        {MODE_LABEL[mode]} · {mad(Number(modes[mode].price || 0) * 100)}
                        {mode === 'NIGHTLY' ? '/nuit' : '/mois'}
                      </span>
                    ))}
                </div>
                <div className="row row--wrap" style={{ gap: 6 }}>
                  {amenities.slice(0, 6).map((amenity) => (
                    <span key={amenity} className="badge">
                      {AMENITY_LABEL[amenity]}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <Notice tone="info">
              {t("Notre équipe visitera le logement sous 48 h avant publication. Vous pouvez continuer à le modifier entre-temps.")}
            </Notice>
          </>
        ) : null}

        <div className="row" style={{ gap: 10 }}>
          {step > 0 ? (
            <button className="btn btn--ghost" onClick={() => setStep((current) => current - 1)}>
              {t("Retour")}
            </button>
          ) : null}
          {step < STEPS.length - 1 ? (
            <button
              className="btn"
              style={{ flex: 1 }}
              disabled={
                busy ||
                (step === 1 && (title.length < 6 || neighborhood.length < 2)) ||
                (step === 2 && enabledModeCount === 0)
              }
              onClick={next}
            >
              {busy ? t("Enregistrement…") : t("Suivant")}
            </button>
          ) : (
            <button className="btn" style={{ flex: 1 }} disabled={busy} onClick={publish}>
              {busy ? t("Envoi…") : t("Publier l’annonce")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
